import React from 'react';
import { FaCube, FaImage, FaCheckCircle } from 'react-icons/fa';

const Step1Upload = ({ state, callbacks, classManager }) => {
  const handleModelUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const extension = fileName.split('.').pop();
      
      const validExtensions = ['gltf', 'glb', 'fbx', 'obj'];
      if (!validExtensions.includes(extension)) {
        alert(`Ungültiger Dateityp: .${extension}\nUnterstützte Formate: .gltf, .glb, .fbx, .obj`);
        return;
      }
      
      // Validiere Dateigröße (max 100MB)
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        alert(`Datei zu groß: ${(file.size / 1024 / 1024).toFixed(2)}MB\nMaximale Größe: 100MB`);
        return;
      }
      
      // Für GLTF-Dateien: Prüfe ob es wirklich eine Textdatei ist
      if (extension === 'gltf') {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const content = e.target.result;
            // Prüfe ob es gültiges JSON ist
            JSON.parse(content);
            // Wenn erfolgreich, lade die Datei
            const url = URL.createObjectURL(file);
            callbacks.setModelUrl(url);
          } catch (error) {
            alert(`Die GLTF-Datei scheint kein gültiges JSON zu sein.\nBitte überprüfen Sie die Datei.\n\nFehler: ${error.message}`);
          }
        };
        reader.onerror = () => {
          alert('Fehler beim Lesen der Datei');
        };
        reader.readAsText(file);
      } else {
        // Für binäre Formate (GLB, FBX, OBJ) direkt laden
        const url = URL.createObjectURL(file);
        callbacks.setModelUrl(url);
      }
    }
  };

  const handleTextureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      callbacks.setTextureUrl(url);
    }
  };

  // Bestimme die aktuelle Klasse basierend auf dem Objekttyp
  const currentClassName = state.objectType === 'model' ? 'object' : 'label';
  const currentClassId = state.objectType === 'model' ? 0 : 1;
  const currentClass = classManager?.classes?.find(c => c.id === currentClassId);

  const handleLabelChange = (e) => {
    const newLabel = e.target.value.trim();
    if (newLabel && classManager && currentClassId !== undefined) {
      classManager.updateClassName(currentClassId, newLabel);
    }
  };

  return (
    <div className="w-full bg-transparent p-0 space-y-4">
      <div>
        <label className="block mb-2 text-xs font-medium text-dark-text-secondary tracking-tight">Objekttyp</label>
        <select 
          value={state.objectType} 
          onChange={(e) => callbacks.setObjectType(e.target.value)}
          className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="model">3D-Modell (.gltf, .glb, .fbx, .obj)</option>
          <option value="texture">2D-Textur (.jpg, .png)</option>
        </select>
      </div>

      <div>
        <label className="block mb-2 text-xs font-medium text-dark-text-secondary tracking-tight">Label (Klasse)</label>
        <input
          type="text"
          value={currentClass?.name || currentClassName}
          onChange={handleLabelChange}
          placeholder={currentClassName}
          className="w-full py-2.5 px-3 bg-white border border-dark-border rounded-lg text-sm text-dark-text transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1"
        />
        <p className="mt-1.5 text-xs text-dark-text-muted">
          Dieser Name wird für die YOLO-Labels verwendet
        </p>
      </div>

      {state.objectType === 'model' && (
        <div>
          <label className="block mb-2 text-xs font-medium text-dark-text-secondary tracking-tight">3D-Modell hochladen</label>
          <div 
            className={`border-2 border-dashed rounded-xl p-6 cursor-pointer transition-all duration-150 flex flex-col items-center justify-center gap-3 min-h-[140px] ${
              state.modelUrl 
                ? 'border-dark-text-muted bg-white' 
                : 'border-dark-border bg-white hover:border-dark-text-muted hover:bg-dark-hover'
            }`}
            onClick={() => document.getElementById('model-upload').click()}
          >
            <div className="text-2xl text-dark-text-muted">
              {state.modelUrl ? <FaCheckCircle className="text-dark-text" /> : <FaCube />}
            </div>
            <div className="text-center">
              {state.modelUrl ? (
                <div className="text-sm text-dark-text font-medium">
                  Modell geladen: {state.modelUrl.split('/').pop()}
                </div>
              ) : (
                <>
                  <div className="text-sm text-dark-text font-medium">Klicken Sie hier, um ein 3D-Modell hochzuladen</div>
                  <div className="text-xs mt-2 text-dark-text-muted">
                    Unterstützte Formate: .gltf, .glb, .fbx, .obj
                  </div>
                </>
              )}
            </div>
            <input
              id="model-upload"
              type="file"
              accept=".gltf,.glb,.fbx,.obj"
              onChange={handleModelUpload}
              className="hidden"
            />
          </div>
        </div>
      )}

      {state.objectType === 'texture' && (
        <div>
          <label className="block mb-2 text-xs font-medium text-dark-text-secondary tracking-tight">2D-Textur hochladen</label>
          <div 
            className={`border-2 border-dashed rounded-xl p-6 cursor-pointer transition-all duration-150 flex flex-col items-center justify-center gap-3 min-h-[140px] ${
              state.textureUrl 
                ? 'border-dark-text-muted bg-white' 
                : 'border-dark-border bg-white hover:border-dark-text-muted hover:bg-dark-hover'
            }`}
            onClick={() => document.getElementById('texture-upload').click()}
          >
            <div className="text-2xl text-dark-text-muted">
              {state.textureUrl ? <FaCheckCircle className="text-dark-text" /> : <FaImage />}
            </div>
            <div className="text-center">
              {state.textureUrl ? (
                <div className="text-sm text-dark-text font-medium">
                  Textur geladen: {state.textureUrl.split('/').pop()}
                </div>
              ) : (
                <>
                  <div className="text-sm text-dark-text font-medium">Klicken Sie hier, um eine 2D-Textur hochzuladen</div>
                  <div className="text-xs mt-2 text-dark-text-muted">
                    Unterstützte Formate: .jpg, .png
                  </div>
                </>
              )}
            </div>
            <input
              id="texture-upload"
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={handleTextureUpload}
              className="hidden"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Step1Upload;
